import SelectionDialog from '@Components/General/SelectionDialog';
import Toolbar from '@Components/General/Toolbar';
import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SimpleSort from '@Components/Common/SimpleSort';
import useStateSimpleSort from '@hooks/useStateSimpleSort';
import useDefaultInfiniteQuery from '@hooks/useDefaultInfiniteQuery';
import {
    emailTemplatesQueryOptions,
    globalEmailTemplatesQueryOptions,
} from '@utils/queryOptions';
import SharedTemplateList from './template-list';
import M3 from '@M3/M3';
import TemplateList from '@Views/Emails/templates/template-select/template-list';
import InfiniteScroll from '@Components/General/InfiniteScroll';
import GridBox from '@Components/General/grid-box';
import TemplateVariants from '@Views/Emails/templates/variants';

const templateTypeMap = {
    user: {
        queryOptions: emailTemplatesQueryOptions,
    },
    shared: {
        queryOptions: globalEmailTemplatesQueryOptions,
    },
};

const UserTemplates = ({
    fetchNextPage,
    isFetchingNextPage,
    templates,
    onSelect,
    isLoading,
}) => {
    const [parentTemplate, setParentTemplate] = useState(null);

    const handleSelect = template => {
        if (template?.variants_count > 1) {
            setParentTemplate(template);
            return;
        }
        onSelect(template.template);
    };

    return (
        <>
            <InfiniteScroll
                ListComponent={GridBox}
                loadMore={fetchNextPage}
                loading={isFetchingNextPage}
            >
                <TemplateList
                    templates={templates}
                    // onSelect={onSelect}
                    onTemplateClick={handleSelect}
                    isLoading={isLoading}
                />
            </InfiniteScroll>
            <TemplateVariants
                selectedTemplate={parentTemplate}
                setSelectedTemplate={setParentTemplate}
                onSelect={handleSelect}
                open={!!parentTemplate}
                onClose={() => setParentTemplate(null)}
            />
        </>
    );
};

const SelectTemplate = ({ onSelect, open, onClose }) => {
    const { t } = useTranslation();
    const [templateType, setTemplateType] = useState('shared');
    const [search, setSearch] = useState('');

    const simpleSortHook = useStateSimpleSort({
        inverseFields: ['created', 'updated'],
        defaultSortField: 'title',
    });

    const params = { search, ordering: simpleSortHook.ordering };

    const queryOptions = templateTypeMap[templateType].queryOptions(params);

    const {
        data: { flatData: templates } = {},
        isLoading,
        fetchNextPage,
        isFetchingNextPage,
    } = useDefaultInfiniteQuery(queryOptions);

    return (
        <M3>
            <SelectionDialog
                open={open}
                onClose={onClose}
                title={t('Select Template')}
                searchProps={{
                    searchPlacholder: t('Search Templates'),
                    onSubmit: setSearch,
                }}
                toolbar={
                    <Toolbar>
                        <Stack direction="row" spacing={2}>
                            <ToggleButtonGroup
                                exclusive
                                value={templateType}
                                onChange={(_, newValue) =>
                                    setTemplateType(newValue)
                                }
                                size="small"
                            >
                                <ToggleButton value="user">
                                    {t('My Templates')}
                                </ToggleButton>
                                <ToggleButton value="shared">
                                    {t('Shared Templates')}
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                        {templateType !== 'shared' && (
                            <SimpleSort
                                sortOptions={[
                                    { value: 'title', label: t('Title') },
                                    {
                                        value: 'created',
                                        label: t('Last Created'),
                                    },
                                    {
                                        value: 'updated',
                                        label: t('Last Updated'),
                                    },
                                ]}
                                {...simpleSortHook}
                            />
                        )}
                    </Toolbar>
                }
            >
                {templateType === 'shared' ? (
                    <SharedTemplateList
                        templates={templates}
                        loadMore={fetchNextPage}
                        loadingNext={isFetchingNextPage}
                        isLoading={isLoading}
                        onSelect={onSelect}
                    />
                ) : (
                    <UserTemplates
                        fetchNextPage={fetchNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        templates={templates}
                        onSelect={onSelect}
                        isLoading={isLoading}
                    />
                )}
            </SelectionDialog>
        </M3>
    );
};

export default SelectTemplate;
